import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { type ActivityRequest } from "../types";
import ActivityCard from "./ActivityCard";
import { XIcon } from "./icons/FeatherIcons";
import { useActivityActions } from "../contexts/ActivityContext";

interface ActivityDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: ActivityRequest | null;
  hideCardActions?: boolean;
}

const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({
  isOpen,
  onClose,
  activity,
  hideCardActions = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { queueUserAction } = useActivityActions();
  const openTimeRef = useRef<number>(0);

  useEffect(() => {
    if (isOpen && activity) {
      // Track activity view start
      openTimeRef.current = Date.now();
    }

    return () => {
      // Track activity view for profile insights - shows interest in activity type/category
      if (openTimeRef.current > 0 && activity) {
        const viewDurationMs = Date.now() - openTimeRef.current;
        queueUserAction(
          "activity_view",
          activity.id,
          activity.title,
          activity.category,
          activity.location,
          {
            engagementLevel:
              viewDurationMs > 10000
                ? "high"
                : viewDurationMs > 3000
                  ? "medium"
                  : "low",
            duration: activity.duration,
            groupSize: activity.groupSize,
          },
        );
        openTimeRef.current = 0;
      }
    };
  }, [isOpen, activity, queueUserAction]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!activity) return null;

  const handleViewDetailsClick = () => {
    onClose();
    navigate(`/activity/${activity.id}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
        >
          <motion.div
            ref={modalRef}
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl shadow-soft-lg overflow-hidden w-full max-w-md max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex-1 overflow-y-auto">
              <ActivityCard
                activity={activity}
                hideActions={true}
                viewMode="compact"
                onCloseModal={onClose}
              />
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleViewDetailsClick}
                className="w-full bg-primary text-white font-bold py-3 px-6 rounded-full shadow-lg shadow-primary/30 transform hover:scale-105 transition-transform"
              >
                View Full Details & Participants
              </button>
            </div>
            <button
              onClick={onClose}
              className="absolute top-2 right-2 bg-black/30 text-white rounded-full p-1.5 hover:bg-black/50 transition-colors z-10"
              aria-label="Close activity details"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActivityDetailModal;
